import React, { useMemo } from 'react'
import {
  LucideIcon, CircleCheckBig } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Tab, type TabIcon } from '../../../hooks/multiWindow'
import { DormantIcon, DormantOpenIcon } from '../../../components/icons/DormantIcon'
import { GoalsIcon } from '../../../components/icons/GoalsIcon'
import { UpcomingIcon, UpcomingOpenIcon } from '../../../components/icons/UpcomingIcon'
import { QuickNotesFilledIcon, QuickNotesEmptyIcon, QuickNotesFilledOpenIcon, QuickNotesEmptyOpenIcon } from '../../../components/icons'
import { useSidebarData } from './SidebarDataContext'
import { RecurringIcon } from '../../../components/icons/RecurringIcon'
// Gewohnheiten/Habits vorübergehend in der Navigation ausgeblendet — Import bleibt für späteres Reaktivieren erhalten
// import { SproutIcon } from '../../../components/icons/SproutIcon'
import { SidebarSection } from './SidebarSection'
import { SidebarItem } from './SidebarItem'
import { ProjectsSection } from './ProjectsSection'
import { FavoritesSection } from './FavoritesSection'
import { dispatchAppEvent } from '@/src/renderer/lib/events'

interface PersonalSidebarContentProps {
  activeTab?: Tab
  openTab: (tab: Tab) => void
  navigateActiveTab: (updates: { path: string; title: string; icon?: TabIcon }) => void
  tabs?: Tab[]
  onCloseTabByPath?: (path: string) => void
  onUnpinByPath?: (path: string) => void
  onPinByPath?: (path: string) => void
  openPreviewTab?: (tab: Omit<Tab, 'id'> & { id?: string }) => void
  promotePreviewBySpace?: (lifeSpace: string) => void
}

export const PersonalSidebarContent: React.FC<PersonalSidebarContentProps> = ({ activeTab, openTab, navigateActiveTab, tabs = [], onCloseTabByPath, onUnpinByPath, onPinByPath, openPreviewTab, promotePreviewBySpace }) => {
  const { t } = useTranslation()
  // Aus dem Halter oberhalb des Wechsels (`SidebarDataProvider`): sonst starteten Zahl und Symbol bei jedem
  // Neu-Einhängen bei `0`/`false` (#285).
  const { quickNotesHasEntries, upcomingCount } = useSidebarData('personal')
  const quickNotesTabOpen = tabs.some(tab => tab.path === '/home/personal/quick-notes')
  const QuickNotesIcon = quickNotesHasEntries
    ? (quickNotesTabOpen ? QuickNotesFilledOpenIcon : QuickNotesFilledIcon)
    : (quickNotesTabOpen ? QuickNotesEmptyOpenIcon : QuickNotesEmptyIcon)
  const QuickNotesTabIcon = quickNotesHasEntries ? QuickNotesFilledIcon : QuickNotesEmptyIcon

  const handleOpenTab = (e: React.MouseEvent, id: string, path: string, title: string, icon?: TabIcon) => {
    if (e.metaKey || e.ctrlKey) {
      openTab({
        id: `${id}-${Date.now()}`,
        path,
        title,
        icon,
        lifeSpace: 'personal',
        isDirty: false,
        isPinned: false
      })
    } else if (openPreviewTab) {
      openPreviewTab({ path, title, icon, lifeSpace: 'personal' })
    } else {
      navigateActiveTab({ path, title, icon })
    }
  }

  const handleDoubleClick = (path: string) => {
    if (previewPaths.has(path)) {
      promotePreviewBySpace?.('personal')
    } else if (!openPaths.has(path)) {
      promotePreviewBySpace?.('personal')
    }
  }

  const handlePromote = promotePreviewBySpace ? () => promotePreviewBySpace('personal') : undefined

  // Path-indexed sets derived from `tabs`. Same memo reason as above —
  // sidebar rerenders often and `tabs` is the only dep.
  const { openPaths, previewPaths, pinnedPaths } = useMemo(() => {
    const open = new Set<string>()
    const preview = new Set<string>()
    const pinned = new Set<string>()
    // A path counts as pinned only when no non-pinned tab also has it.
    const hasUnpinned = new Set<string>()
    for (const tab of tabs) {
      open.add(tab.path)
      if (tab.isPreview) preview.add(tab.path)
      if (tab.isPinned) pinned.add(tab.path)
      else hasUnpinned.add(tab.path)
    }
    for (const path of hasUnpinned) pinned.delete(path)
    return { openPaths: open, previewPaths: preview, pinnedPaths: pinned }
  }, [tabs])
  const DormantSidebarIcon = openPaths.has('/home/personal/dormant') ? DormantOpenIcon : DormantIcon
  const UpcomingSidebarIcon = openPaths.has('/home/personal/upcoming') ? UpcomingOpenIcon : UpcomingIcon

  return (
    <>
      <div className="standard-sidebar__quick-notes">
        <SidebarItem
          icon={QuickNotesIcon as unknown as LucideIcon}
          label={t('sidebar.quickNotes')}
          path="/home/personal/quick-notes"
          isActive={activeTab?.path === '/home/personal/quick-notes'}
          hasOpenTab={openPaths.has('/home/personal/quick-notes')}
          isPreview={previewPaths.has('/home/personal/quick-notes')}
          isPinned={pinnedPaths.has('/home/personal/quick-notes')}
          onUnpin={onUnpinByPath ? () => onUnpinByPath('/home/personal/quick-notes') : undefined}
          onPinTab={onPinByPath ? () => onPinByPath('/home/personal/quick-notes') : undefined}
          onCloseTab={onCloseTabByPath ? () => onCloseTabByPath('/home/personal/quick-notes') : undefined}
          onOpenNewTab={() => openTab({ id: `personal-quick-notes-${Date.now()}`, path: '/home/personal/quick-notes', title: t('sidebar.quickNotes'), icon: QuickNotesTabIcon as unknown as LucideIcon, lifeSpace: 'personal', isDirty: false, isPinned: false })}
          onClick={(e) => { if (!e.metaKey && !e.ctrlKey) dispatchAppEvent('quick-notes-exit-clarify', 'personal'); handleOpenTab(e, 'personal-quick-notes', '/home/personal/quick-notes', t('sidebar.quickNotes'), QuickNotesTabIcon as unknown as LucideIcon) }}
          onDoubleClick={() => handleDoubleClick('/home/personal/quick-notes')}
          onPromote={handlePromote}
        />
      </div>
      <SidebarSection
        id="personal-overview"
        title={t('sidebar.quickOverview')}
        defaultExpanded
      >
        <SidebarItem
          icon={UpcomingSidebarIcon}
          label={t('sidebar.upcoming')}
          badge={upcomingCount}
          path="/home/personal/upcoming"
          isActive={activeTab?.path === '/home/personal/upcoming'}
          hasOpenTab={openPaths.has('/home/personal/upcoming')}
          isPreview={previewPaths.has('/home/personal/upcoming')}
          isPinned={pinnedPaths.has('/home/personal/upcoming')}
          onUnpin={onUnpinByPath ? () => onUnpinByPath('/home/personal/upcoming') : undefined}
          onPinTab={onPinByPath ? () => onPinByPath('/home/personal/upcoming') : undefined}
          onCloseTab={onCloseTabByPath ? () => onCloseTabByPath('/home/personal/upcoming') : undefined}
          onOpenNewTab={() => openTab({ id: `personal-upcoming-${Date.now()}`, path: '/home/personal/upcoming', title: t('sidebar.upcoming'), icon: UpcomingIcon as unknown as LucideIcon, lifeSpace: 'personal', isDirty: false, isPinned: false })}
          onClick={(e) => handleOpenTab(e, 'personal-upcoming', '/home/personal/upcoming', t('sidebar.upcoming'), UpcomingIcon as unknown as LucideIcon)}
          onDoubleClick={() => handleDoubleClick('/home/personal/upcoming')}
          onPromote={handlePromote}
        />
        <SidebarItem
          icon={DormantSidebarIcon as LucideIcon}
          label={t('sidebar.dormant')}
          path="/home/personal/dormant"
          isActive={activeTab?.path === '/home/personal/dormant'}
          hasOpenTab={openPaths.has('/home/personal/dormant')}
          isPreview={previewPaths.has('/home/personal/dormant')}
          isPinned={pinnedPaths.has('/home/personal/dormant')}
          onUnpin={onUnpinByPath ? () => onUnpinByPath('/home/personal/dormant') : undefined}
          onPinTab={onPinByPath ? () => onPinByPath('/home/personal/dormant') : undefined}
          onCloseTab={onCloseTabByPath ? () => onCloseTabByPath('/home/personal/dormant') : undefined}
          onOpenNewTab={() => openTab({ id: `personal-dormant-${Date.now()}`, path: '/home/personal/dormant', title: t('sidebar.dormant'), icon: DormantIcon as LucideIcon, lifeSpace: 'personal', isDirty: false, isPinned: false })}
          onClick={(e) => handleOpenTab(e, 'personal-dormant', '/home/personal/dormant', t('sidebar.dormant'), DormantIcon as LucideIcon)}
          onDoubleClick={() => handleDoubleClick('/home/personal/dormant')}
          onPromote={handlePromote}
        />
        <SidebarItem
          icon={CircleCheckBig}
          label={t('sidebar.done')}
          path="/home/personal/done"
          isActive={activeTab?.path === '/home/personal/done'}
          hasOpenTab={openPaths.has('/home/personal/done')}
          isPreview={previewPaths.has('/home/personal/done')}
          isPinned={pinnedPaths.has('/home/personal/done')}
          onUnpin={onUnpinByPath ? () => onUnpinByPath('/home/personal/done') : undefined}
          onPinTab={onPinByPath ? () => onPinByPath('/home/personal/done') : undefined}
          onCloseTab={onCloseTabByPath ? () => onCloseTabByPath('/home/personal/done') : undefined}
          onOpenNewTab={() => openTab({ id: `personal-done-${Date.now()}`, path: '/home/personal/done', title: t('sidebar.done'), icon: CircleCheckBig, lifeSpace: 'personal', isDirty: false, isPinned: false })}
          onClick={(e) => handleOpenTab(e, 'personal-done', '/home/personal/done', t('sidebar.done'), CircleCheckBig)}
          onDoubleClick={() => handleDoubleClick('/home/personal/done')}
          onPromote={handlePromote}
        />
      </SidebarSection>

      <SidebarSection
        id="personal-development"
        title={t('sidebar.personalSection')}
        defaultExpanded
      >
        {/* Gewohnheiten/Habits vorübergehend ausgeblendet — Route bleibt aktiv, Eintrag später wieder einkommentieren
        <SidebarItem
          icon={SproutIcon as unknown as LucideIcon}
          label={t('sidebar.habits')}
          path="/home/personal/habits"
          isActive={activeTab?.path === '/home/personal/habits'}
          hasOpenTab={openPaths.has('/home/personal/habits')}
          isPreview={previewPaths.has('/home/personal/habits')}
          isPinned={pinnedPaths.has('/home/personal/habits')}
          onUnpin={onUnpinByPath ? () => onUnpinByPath('/home/personal/habits') : undefined}
          onPinTab={onPinByPath ? () => onPinByPath('/home/personal/habits') : undefined}
          onCloseTab={onCloseTabByPath ? () => onCloseTabByPath('/home/personal/habits') : undefined}
          onOpenNewTab={() => openTab({ id: `personal-habits-${Date.now()}`, path: '/home/personal/habits', title: t('sidebar.habits'), icon: SproutIcon as unknown as LucideIcon, lifeSpace: 'personal', isDirty: false, isPinned: false })}
          onClick={(e) => handleOpenTab(e, 'personal-habits', '/home/personal/habits', t('sidebar.habits'), SproutIcon as unknown as LucideIcon)}
          onDoubleClick={() => handleDoubleClick('/home/personal/habits')}
          onPromote={handlePromote}
        />
        */}
        <SidebarItem
          icon={RecurringIcon as unknown as LucideIcon}
          label={t('sidebar.recurring')}
          path="/home/personal/recurring"
          isActive={activeTab?.path === '/home/personal/recurring'}
          hasOpenTab={openPaths.has('/home/personal/recurring')}
          isPreview={previewPaths.has('/home/personal/recurring')}
          isPinned={pinnedPaths.has('/home/personal/recurring')}
          onUnpin={onUnpinByPath ? () => onUnpinByPath('/home/personal/recurring') : undefined}
          onPinTab={onPinByPath ? () => onPinByPath('/home/personal/recurring') : undefined}
          onCloseTab={onCloseTabByPath ? () => onCloseTabByPath('/home/personal/recurring') : undefined}
          onOpenNewTab={() => openTab({ id: `personal-recurring-${Date.now()}`, path: '/home/personal/recurring', title: t('sidebar.recurring'), icon: RecurringIcon as unknown as LucideIcon, lifeSpace: 'personal', isDirty: false, isPinned: false })}
          onClick={(e) => handleOpenTab(e, 'personal-recurring', '/home/personal/recurring', t('sidebar.recurring'), RecurringIcon as unknown as LucideIcon)}
          onDoubleClick={() => handleDoubleClick('/home/personal/recurring')}
          onPromote={handlePromote}
        />
        <SidebarItem
          icon={GoalsIcon}
          label={t('sidebar.goals')}
          path="/home/personal/goals"
          isActive={activeTab?.path === '/home/personal/goals'}
          hasOpenTab={openPaths.has('/home/personal/goals')}
          isPreview={previewPaths.has('/home/personal/goals')}
          isPinned={pinnedPaths.has('/home/personal/goals')}
          onUnpin={onUnpinByPath ? () => onUnpinByPath('/home/personal/goals') : undefined}
          onPinTab={onPinByPath ? () => onPinByPath('/home/personal/goals') : undefined}
          onCloseTab={onCloseTabByPath ? () => onCloseTabByPath('/home/personal/goals') : undefined}
          onOpenNewTab={() => openTab({ id: `personal-goals-${Date.now()}`, path: '/home/personal/goals', title: t('sidebar.goals'), icon: GoalsIcon, lifeSpace: 'personal', isDirty: false, isPinned: false })}
          onClick={(e) => handleOpenTab(e, 'personal-goals', '/home/personal/goals', t('sidebar.goals'), GoalsIcon)}
          onDoubleClick={() => handleDoubleClick('/home/personal/goals')}
          onPromote={handlePromote}
        />
      </SidebarSection>

      <FavoritesSection
        lifeSpace="personal"
        activeTab={activeTab}
        openTab={openTab}
        onCloseTabByPath={onCloseTabByPath}
        onUnpinByPath={onUnpinByPath}
        onPinByPath={onPinByPath}
        openPaths={openPaths}
        previewPaths={previewPaths}
        pinnedPaths={pinnedPaths}
        onOpenTab={handleOpenTab}
        onDoubleClick={handleDoubleClick}
        onPromote={handlePromote}
      />

      <ProjectsSection
        lifeSpace="personal"
        activeTab={activeTab}
        openTab={openTab}
        navigateActiveTab={navigateActiveTab}
        onCloseTabByPath={onCloseTabByPath}
        onUnpinByPath={onUnpinByPath}
        onPinByPath={onPinByPath}
        openPaths={openPaths}
        previewPaths={previewPaths}
        pinnedPaths={pinnedPaths}
        onOpenTab={handleOpenTab}
        onDoubleClick={handleDoubleClick}
        onPromote={handlePromote}
      />
    </>
  )
}
